import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Download, X } from 'lucide-react';
import { usePwaInstall } from '../utilities/pwaInstall';

// Pages where the banner may appear (the login page has its own install button)
const BANNER_PATHS = ['/role-dashboard', '/employee-dashboard'];

const SNOOZE_KEY = 'installBannerSnoozedUntil';
const SNOOZE_DAYS = 7;

const isSnoozed = () => {
    try {
        return Date.now() < Number(localStorage.getItem(SNOOZE_KEY) || 0);
    } catch {
        return false;
    }
};

const snooze = () => {
    try {
        localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_DAYS * 24 * 60 * 60 * 1000));
    } catch {
        // storage blocked — banner just hides for this page view
    }
};

const InstallAppBanner = () => {
    const { pathname } = useLocation();
    const { canInstall, install } = usePwaInstall();
    const [hidden, setHidden] = useState(isSnoozed);

    if (!canInstall || hidden || !BANNER_PATHS.includes(pathname)) return null;

    const handleNotNow = () => {
        snooze();
        setHidden(true);
    };

    const handleInstall = async () => {
        const outcome = await install();
        // Declined in the browser dialog: treat like "Not now" so we don't nag
        if (outcome === 'dismissed') snooze();
        setHidden(true);
    };

    return (
        <div
            role="region"
            aria-label="Install My Essel app"
            className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 w-[calc(100%-2rem)] max-w-xl
                       flex items-center gap-3 px-4 py-3 rounded-xl shadow-2xl
                       bg-[#0d1b5e] dark:bg-gray-800 border border-orange-400/40 text-white"
        >
            <div className="shrink-0 bg-gradient-to-r from-blue-900 to-orange-500 p-2 rounded-lg">
                <Download className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold leading-tight">Install My Essel on this device</p>
                <p className="text-xs text-white/70 leading-tight mt-0.5">
                    Open it in one click from your desktop or taskbar, like a normal app.
                </p>
            </div>
            <button
                onClick={handleInstall}
                className="shrink-0 px-3 py-1.5 text-xs font-semibold rounded-lg bg-orange-500 hover:bg-orange-600 transition-colors"
            >
                Install
            </button>
            <button
                onClick={handleNotNow}
                className="shrink-0 px-2 py-1.5 text-xs text-white/70 hover:text-white transition-colors"
            >
                Not now
            </button>
            <button
                onClick={handleNotNow}
                aria-label="Close"
                className="shrink-0 text-white/50 hover:text-white transition-colors"
            >
                <X className="h-4 w-4" />
            </button>
        </div>
    );
};

export default InstallAppBanner;
